// Client-side photo shrinking so big phone pictures stay well under the 10 MB limit and upload faster.
export const COMPRESS_MIN_BYTES = 1 * 1024 * 1024; // leave small images untouched
export const MAX_EDGE = 2048;
const TARGET_BYTES = 2 * 1024 * 1024;
const COMPRESSIBLE = new Set(["image/jpeg", "image/png", "image/webp"]);

export const shouldCompress = (file) => COMPRESSIBLE.has(file.type) && file.size > COMPRESS_MIN_BYTES;

async function decode(file) {
  if (typeof createImageBitmap === "function") {
    try { return await createImageBitmap(file, { imageOrientation: "from-image" }); } catch { /* fall through */ }
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("decode failed")); };
    img.src = url;
  });
}

const toBlob = (canvas, type, quality) => new Promise((resolve) => canvas.toBlob(resolve, type, quality));

// Returns a smaller File (JPEG/WEBP kept; large PNG photos become JPEG) or the original when shrinking doesn't help.
export async function compressImage(file, { maxEdge = MAX_EDGE, targetBytes = TARGET_BYTES } = {}) {
  if (!shouldCompress(file)) return file;
  let bitmap;
  try { bitmap = await decode(file); } catch { return file; }
  const w = bitmap.width, h = bitmap.height;
  const scale = Math.min(1, maxEdge / Math.max(w, h));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(w * scale));
  canvas.height = Math.max(1, Math.round(h * scale));
  canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close?.();
  const outType = file.type === "image/webp" ? "image/webp" : "image/jpeg";
  let blob = null;
  for (const q of [0.85, 0.75, 0.65, 0.55]) {
    blob = await toBlob(canvas, outType, q);
    if (!blob || blob.size <= targetBytes) break;
  }
  if (!blob || blob.size >= file.size) return file;
  const name = outType === "image/jpeg" ? file.name.replace(/\.(png|jpe?g|webp)$/i, "") + ".jpg" : file.name;
  const out = new File([blob], name, { type: outType, lastModified: Date.now() });
  out.originalSize = file.size;
  return out;
}
